/**
 * Per-cluster fix sub-agent runner — MULTI-FILE batch repair.
 *
 * The fix loop no longer forces a single "primary file". A cluster carries
 * `relevantFiles` (production code AND the failing test files, no ordering); the
 * fix agent decides for itself which of them is actually wrong and edits them
 * with MINIMAL SEARCH/REPLACE blocks, in ONE LLM call using the batch-edit
 * envelope (`{ files: [{ path, blocks: [{ search, replace }], notes }] }`).
 * An EXISTING file is NEVER rewritten wholesale — whole content is used ONLY
 * to CREATE a brand-new file (a single block with `search: ''`, gated to
 * creatable/non-existent candidates). There is no whole-file fallback: if
 * surgical edits can't land a fix within the inner loop's attempts, the
 * cluster stays red and diagnose/agentic recovery re-routes it.
 *
 * Pipeline per call:
 *   build neutral multi-file prompt (prompt.ts)
 *     → one llmCall (purpose:'analysis', cache:true, BATCH_EDIT_SCHEMA)
 *     → parseBatchEditOutput + reconcileBatchEdit against cluster.relevantFiles
 *       (so only candidate files are written, in REQUEST order — deterministic)
 *     → per accepted file: apply blocks via SearchReplaceStrategy (or the
 *       create-via-empty-search path), then lintHallucinatedAPI + the
 *       import-resolution gate — reject (drop) any file that fails
 *     → if any accepted spec's assertions changed, run the assertion-reflection
 *       step (assertionReflection.ts) and MERGE its edits in (reflection wins on
 *       path conflict)
 *     → return FixResult { changedFiles, raw, editAttempts, runbook? }
 */

import type {
  ChangedFile,
  DesignSpecPayload,
  EventEmitter,
  FailingTest,
  FixAttemptRecord,
  LLMConfig,
  ProjectFile,
} from '../../types';
import type { StreamLlmCall } from '../../testing/mockLlm';
import type { DiagnosedCluster } from '../diagnose/parser';
import { callLLMStream } from '../../llm';
import { buildFixPrompt } from './prompt';
import { contextBudget } from './contextBudget';
import { stripSlash, computeMissingImportTargets } from '../pathUtils';
import { extractFile, resolveImportPath } from '../../knowledge/extractor';
import type { RunbookCandidate } from '../../runbooks/types';
import { requestRunbookCandidate } from '../../runbooks/authoring';
import { detectTestContradictions, type TestContradiction } from '../contradiction';
import { elideFileStructurally } from '../structuralElide';
import { extractFailureSymbols } from '../../knowledge/crossReference';
import { lintHallucinatedAPI } from './hallucinationLint';
import {
  BATCH_EDIT_SCHEMA,
  parseBatchEditOutput,
  reconcileBatchEdit,
  type BatchEditFile,
} from '../../prompts/batchWrite';
import { SearchReplaceStrategy } from '../editStrategies/SearchReplaceStrategy';
import type { EditAttempt } from '../editStrategies';
import {
  reflectOnTestEdits,
  extractAssertions,
  isSpecPath,
  type EditedSpec,
} from './assertionReflection';

// Per-attempt JSONL trace for offline debugging. Env-gated; resolved lazily so
// the browser bundle stays clean (see the long note this replaces — same FS
// gating, kept verbatim in intent).
const FIX_TRACE_PATH =
  typeof process !== 'undefined' && process.env ? process.env.GOTTI_FIX_TRACE_PATH : undefined;

let appendFileSyncFn: typeof import('fs').appendFileSync | null = null;
if (FIX_TRACE_PATH) {
  void import('node:fs')
    .then((fs) => {
      appendFileSyncFn = fs.appendFileSync;
    })
    .catch((err) => {
      console.warn(
        `[fixPlanner/fix] trace disabled (fs load failed): ${err instanceof Error ? err.message : String(err)}`,
      );
    });
}

function appendFixTrace(record: Record<string, unknown>): void {
  if (!FIX_TRACE_PATH || !appendFileSyncFn) return;
  try {
    appendFileSyncFn(FIX_TRACE_PATH, JSON.stringify(record) + '\n', 'utf8');
  } catch (err) {
    console.warn(`[fixPlanner/fix] trace write failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

function excerpt(content: string, maxLines: number): string {
  const lines = content.split('\n');
  if (lines.length <= maxLines) return content;
  return lines.slice(0, maxLines).join('\n') + `\n// … (+${lines.length - maxLines} more lines)`;
}

const MAX_FILE_INCLUDE_LINES = 400;

/** Input budget for the assembled fix prompt (system+user), in ~4-chars-per-
 *  token estimated tokens. 90k leaves a safety margin under the 100k target —
 *  see the guard in runClusterFix right after buildFixPrompt. */
const FIX_PROMPT_TOKEN_BUDGET = 90_000;

// The hallucinated-API lint moved to ./hallucinationLint so other modules can
// call it; re-exported here for back-compat with existing imports.
export { lintHallucinatedAPI };

export interface FixOptions {
  cluster: DiagnosedCluster;
  /** Resolved cluster.failureIndices → real FailingTest values. */
  failures: FailingTest[];
  workingFiles: ProjectFile[];
  llm: LLMConfig;
  signal?: AbortSignal;
  emit?: EventEmitter;
  nodeId?: string;
  /** Inject a custom LLM call function (for testing). Defaults to `callLLMStream`. */
  llmCall?: StreamLlmCall;
  /** Rolling window of prior fix iterations — drives the anti-loop rules. */
  fixAttemptHistory: FixAttemptRecord[];
  /** When true, author a generalizable runbook candidate after a winning edit. */
  enableRunbooks?: boolean;
  /** Design specs from the design phase — rendered (all) into the fix prompt. */
  designDocs?: readonly DesignSpecPayload[];
  /** The user's requested change when fixing inside a behaviour-edit flow. */
  userIntent?: string;
  /** Closed inner loop attempt index (0 = first try). */
  retryAttempt?: number;
  /** Fix-prompt context size (#1). 0 = slim (default), 1 = mid, 2 = full. */
  contextLevel?: number;
  /** REGRESSION GUARD: spec file paths of previously-passing tests the prior
   *  edit broke. Their content is injected as "RESTORE THESE" guardrails. */
  regressedSpecPaths?: readonly string[];
  /** Names of those broken tests, for the regression banner. */
  regressedTestNames?: readonly string[];
  /** Deterministic cross-test contradiction signal (see `../contradiction.ts`),
   *  forwarded verbatim into the fix prompt's shorter CONTRADICTION SUSPECT
   *  block. Absent/empty ⇒ no block (byte-identical prompt). */
  contradictions?: TestContradiction[];
}

export interface FixResult {
  /** Files the fix batch (+ any reflection) decided to change, in a
   *  deterministic order. Empty when no file was accepted. */
  changedFiles: ChangedFile[];
  /** Raw model output — used only for error logging. */
  raw: string;
  /** Per-file outcomes. Successes always report `search-replace` — there is
   *  no whole-file edit path (whole content is used only to CREATE a
   *  brand-new file, still tallied as `search-replace`). Surfaces
   *  accept/reject reasons. */
  editAttempts: EditAttempt[];
  /** Runbook candidate authored on success when `enableRunbooks` is set. */
  runbook?: RunbookCandidate;
}

function fileExcerpt(content: string, maxLines: number = MAX_FILE_INCLUDE_LINES): string {
  const lines = content.split('\n');
  if (lines.length <= maxLines) return content;
  return lines.slice(0, maxLines).join('\n') + `\n// … (${lines.length - maxLines} more lines truncated)`;
}

/**
 * Shrink guard (ported from WholeFileStrategy): a "full contents" rewrite that
 * collapses a large file almost always means the model reconstructed from a
 * partial view — a real data-loss event. Files >300 lines may not shrink below
 * ~40%. Returns null when OK, or a reason string when the file must be rejected.
 */
/**
 * The three apply-time content gates, shared by EVERY writer that lands edits
 * into the working set — the batch fix path below AND the agentic recovery
 * path (agenticFix/runner.ts). No writer class is exempt: an un-gated writer
 * is how a hallucinated bare import (`chesslabs/testing`) compile-dropped a
 * spec file and minted a hollow green. Returns a rejection reason or null.
 */
export function validateEditGates(
  path: string,
  original: string,
  newCode: string,
  knownPaths: Set<string>,
): string | null {
  const lint = lintHallucinatedAPI(newCode);
  if (!lint.ok) return `hallucinated API: this.${lint.hit}(...) at line ${lint.line}`;
  const shrink = shrinkGuard(original, newCode);
  if (shrink) return shrink;
  const unresolvable = firstUnresolvableImport(path, newCode, knownPaths);
  if (unresolvable) {
    return `unresolvable import '${unresolvable}' — not a workspace module, not creatable here, and not an allowed external (vitest, @gotti/*)`;
  }
  return null;
}

function shrinkGuard(original: string, proposed: string): string | null {
  const originalLines = original.split('\n').length;
  const proposedLines = proposed.split('\n').length;
  if (originalLines > 300 && proposedLines < originalLines * 0.4) {
    return `whole-file output shrank ${originalLines} → ${proposedLines} lines — files >300 lines may not shrink below 40% (unseen-tail data-loss guard)`;
  }
  return null;
}

/** Net-deletion threshold above which a surgical edit's result is LOGGED (never
 *  rejected — a block only ever touches text it quoted, so this can't be the
 *  same "unseen tail" data-loss class the whole-file shrink-guard defends
 *  against; it's just a heads-up for review). */
const NET_DELETION_WARN_LINES = 15;

function warnOnLargeNetDeletion(clusterId: string, path: string, original: string, newCode: string): void {
  const delta = newCode.split('\n').length - original.split('\n').length;
  if (delta <= -NET_DELETION_WARN_LINES) {
    console.warn(
      `[fixPlanner/fix] cluster=${clusterId}: ${path} surgical edit net-deleted ${-delta} lines — logged only (not rejected); verify this wasn't unintended data loss.`,
    );
  }
}

/**
 * Import-resolution gate: return the first project-internal import specifier
 * in `newCode` that resolves to NO file in `knownPaths` (workspace ∪ this
 * batch), or null when every project-internal import resolves. External/bare
 * specifiers (vitest, @gotti/*, lodash, …) are never project-internal — only
 * `@/`, `./`, `../` are checked (mirrors resolveImportPath/
 * computeMissingImportTargets's own classification, so this can't drift from
 * the creatable-candidate machinery above).
 */
/** Bare (package) specifiers a generated file may legitimately import. Anything
 *  else bare is a hallucinated package (observed live: `chesslabs/testing`,
 *  `@gotti/testing`) — the sandbox transpiler can't resolve it, the file
 *  compile-drops, and the suite silently shrinks. Reject at apply time instead.
 *
 *  The `@gotti/*` entries are the EXACT set of runtime-shimmed externals the
 *  runners register in `externalModules` (NodeRunnerAdapter + the sandbox app's
 *  SandboxRuntime/SandboxRunner — kept in parity). A bare-prefix `@gotti/` used
 *  to be allowed, which let a hallucinated `@gotti/testing` (with an invented
 *  `createTestWorld`/`stepWorld` API) pass the gate: a fix REWROTE a real
 *  `@gotti/sandbox-test` spec into that fake harness, the file failed to load,
 *  its tests dropped from the suite, and the loop minted a hollow "30/30" green
 *  the authoritative run contradicted (34/36). Exact-name matching (or a real
 *  package's subpath) blocks that regression at apply time. */
const ALLOWED_BARE_IMPORTS = [
  'vitest',
  '@gotti/sandbox-test',
  '@gotti/client',
  '@gotti/server',
  '@gotti/core',
  '@gotti/systems',
  '@gotti/test-utils',
];

function firstUnresolvableImport(path: string, newCode: string, knownPaths: Set<string>): string | null {
  for (const imp of extractFile(newCode).imports) {
    const src = imp.source;
    if (!src.startsWith('@/') && !src.startsWith('./') && !src.startsWith('../')) {
      // Bare package specifier — must be an exact known-shimmed external (or a
      // subpath of one, e.g. `@gotti/core/foo`). NOT a loose `@gotti/` prefix:
      // that admitted hallucinated `@gotti/<madeup>` packages.
      if (ALLOWED_BARE_IMPORTS.some((a) => src === a || src.startsWith(`${a}/`))) continue;
      return src;
    } else {
      if (resolveImportPath(path, src, knownPaths)) continue;
      return src;
    }
  }
  return null;
}

export async function runClusterFix(opts: FixOptions): Promise<FixResult> {
  const activity = (message: string): void => {
    if (opts.emit && opts.nodeId) void opts.emit({ type: 'node:activity', id: opts.nodeId, message });
  };
  const budget = contextBudget(opts.contextLevel ?? 0);

  // The cluster's WRITABLE candidate set = relevantFiles (code AND test files),
  // in diagnose order. Build the per-file content map (only files that exist),
  // de-duplicated, in a stable order. This is the request order reconcileBatchEdit
  // will apply in — deterministic regardless of model output ordering.
  const fileMap = new Map<string, string>();
  for (const f of opts.workingFiles) fileMap.set(stripSlash(f.path), f.content);

  // Compiler-named unresolved-import targets — a cluster relevantFile that is
  // NOT in fileMap is admitted as a CREATABLE candidate iff it is one of
  // these (compare on stripped paths). Without this, a cluster telling the
  // model to CREATE the missing module it names (e.g. a TS2307 isolated
  // cluster, see typeErrorDiagnosis.ts) has its write silently dropped by
  // the `fileMap.has(k)` guard below — 5 byte-identical no-op iterations,
  // then a plateau bail. Computed once per call, pure function of workingFiles.
  const missingImportTargets = computeMissingImportTargets(opts.workingFiles);
  const creatableByPath = new Map<string, string[]>(); // stripped path -> importer paths
  for (const m of missingImportTargets) creatableByPath.set(stripSlash(m.guessPath), m.importers);

  const candidatePaths: string[] = [];
  const creatableCandidates = new Set<string>();
  const seenCandidate = new Set<string>();
  for (const p of opts.cluster.relevantFiles) {
    const k = stripSlash(p);
    if (seenCandidate.has(k)) continue;
    if (!fileMap.has(k)) {
      if (!creatableByPath.has(k)) continue; // candidate must exist OR be a creatable missing-import target
      creatableCandidates.add(k);
    }
    seenCandidate.add(k);
    candidatePaths.push(k);
  }
  // Also surface the failing-test files even if diagnose omitted them from
  // relevantFiles — the agent needs the test contract in front of it, and they
  // are valid edit targets. Stable: candidate order first, then failing-test
  // files in `failures` order.
  for (const f of opts.failures) {
    const k = stripSlash(f.file);
    if (seenCandidate.has(k)) continue;
    if (!fileMap.has(k)) {
      if (!creatableByPath.has(k)) continue;
      creatableCandidates.add(k);
    }
    seenCandidate.add(k);
    candidatePaths.push(k);
  }

  // Extract failure symbols once — used to pin implicated bodies in elision.
  const failureSymbols = extractFailureSymbols(opts.failures);

  // Render the full contents of every involved file. Each is editable; we want
  // every export/signature visible even on long files, so elide structurally
  // when over budget (signatures + implicated bodies survive). A file at/under
  // budget is rendered verbatim. Order = candidatePaths (deterministic).
  const relevantFiles = new Map<string, string>();
  for (const k of candidatePaths) {
    if (creatableCandidates.has(k)) {
      // No content to show — a tiny placeholder naming the canonical path and
      // who imports it is all the model needs (see CLAUDE.md's "no elision
      // path needed — it's tiny" note for this exact case).
      const importers = creatableByPath.get(k) ?? [];
      relevantFiles.set(
        k,
        `// FILE DOES NOT EXIST YET — the import(s) below resolve to this canonical path.\n` +
          `// CREATE this file in your response. Importers: ${importers.join(', ')}`,
      );
      continue;
    }
    const content = fileMap.get(k)!;
    if (content.split('\n').length <= budget.suspectMaxLines) {
      relevantFiles.set(k, content);
    } else {
      const r = elideFileStructurally(content, { keepSymbols: failureSymbols, maxLines: budget.suspectMaxLines });
      relevantFiles.set(k, r.text);
      if (!r.truncated) {
        // No elidable bodies — rendered verbatim despite being over budget.
        console.log(`[fixPlanner/fix] ${k} rendered VERBATIM over budget (no elidable bodies, ${r.keptLines} lines) — watch prompt size`);
      }
    }
  }

  // Test-file contents (the failing tests' specs) — passed separately so the
  // prompt can call out the contract; these paths are also in relevantFiles.
  const testFileContents = new Map<string, string>();
  for (const f of opts.failures) {
    const path = stripSlash(f.file);
    if (testFileContents.has(path)) continue;
    const content = fileMap.get(path);
    if (!content) continue;
    testFileContents.set(path, fileExcerpt(content, budget.failingSpecMaxLines));
  }

  const bugCount = opts.cluster.subProblems?.length ?? 1;
  activity(
    `Reading ${candidatePaths.length} involved file(s) + the contract of ${opts.failures.length} failing test(s) to fix ${bugCount} bug(s)…`,
  );

  // READ-ONLY reference files (e.g. the correct client kept for its pixel→cell
  // math when the cluster was narrowed to a test-only fix). Shown for context,
  // never editable.
  const referenceFileContents = new Map<string, string>();
  for (const p of opts.cluster.referenceFiles ?? []) {
    const k = stripSlash(p);
    const content = fileMap.get(k);
    if (content && !referenceFileContents.has(k)) referenceFileContents.set(k, content);
  }

  // Assemble the fix prompt. Design specs are no longer pre-filtered here —
  // buildFixPrompt ALWAYS scopes `opts.designDocs` down to the subset relevant
  // to this cluster's involved files (selectRelevantDesignDocs, with a floor
  // for 'overview' docs), so passing the full set is correct and there's
  // nothing left for this call site to filter redundantly.
  const built = buildFixPrompt({
    cluster: opts.cluster,
    failures: opts.failures,
    relevantFiles,
    testFileContents,
    referenceFileContents,
    allFiles: opts.workingFiles,
    fixAttemptHistory: opts.fixAttemptHistory,
    designDocs: opts.designDocs ?? [],
    userIntent: opts.userIntent,
    retryAttempt: opts.retryAttempt,
    maxFailures: budget.maxFailures,
    maxLogsPerFailure: budget.maxLogsPerFailure,
    regressedGuardrails: (opts.regressedSpecPaths ?? [])
      .map((p) => ({ path: stripSlash(p), content: fileMap.get(stripSlash(p)) ?? '' }))
      .filter((g) => g.content.length > 0 && !testFileContents.has(g.path)),
    regressedTestNames: opts.regressedTestNames,
    contradictions: opts.contradictions,
  });

  // INPUT BUDGET GUARD (~90k tokens, safety margin under the 100k target):
  // now purely an operator signal. Design-doc scoping (the biggest lever this
  // guard used to pull) already happened unconditionally inside
  // buildFixPrompt, so there's no further rebuild to attempt — the failing-
  // test contract and the suspect file bodies are never truncated. Still logs
  // loudly if a cluster with many/huge suspect files is over budget even
  // after scoping, so it doesn't fail silently.
  // Includes cachePrefix (the quickref) in the size estimate — it's still
  // SENT on every call (just cache-discounted when it hits).
  const estimateTokens = (s: string, u: string, c: string): number =>
    Math.ceil((s.length + u.length + c.length) / 4);
  if (estimateTokens(built.system, built.user, built.cachePrefix) > FIX_PROMPT_TOKEN_BUDGET) {
    console.warn(
      `[fixPlanner/fix] WARNING: cluster=${opts.cluster.id} input ~${estimateTokens(built.system, built.user, built.cachePrefix)}tok ` +
        `exceeds the ${FIX_PROMPT_TOKEN_BUDGET}tok budget even after per-cluster design-spec scoping — ` +
        `proceeding anyway (failing tests + suspect files are never truncated).`,
    );
  }
  const { system, user, cachePrefix } = built;

  // FIX-MODEL SPEED SPLIT: GOTTI_FIX_MODEL overrides ONLY the edit call (the
  // smart diagnose model + initial code-gen model are untouched). The model
  // field is excluded from the request hash, so checkpoint replay stays exact.
  const fixModel = process.env['GOTTI_FIX_MODEL'];
  const editLlm = fixModel ? { ...opts.llm, model: fixModel } : opts.llm;

  const fixStart = Date.now();
  console.log(
    `[fixPlanner/fix] START cluster=${opts.cluster.id} candidates=${candidatePaths.length} ` +
      `failures=${opts.failures.length} userPromptBytes=${user.length}`,
  );
  activity(`Asking the model to rewrite whichever of the ${candidatePaths.length} involved file(s) are wrong…`);

  let raw: string;
  try {
    raw = await callOrInjected(opts.llmCall)({
      system,
      user,
      ...editLlm,
      purpose: 'analysis',
      cache: true,
      cachePrefix,
      schema: BATCH_EDIT_SCHEMA,
      signal: opts.signal,
      onChunk: (delta, accumulated) => {
        if (opts.emit && opts.nodeId) {
          void opts.emit({ type: 'node:chunk', id: opts.nodeId, delta, accumulatedLength: accumulated.length });
        }
      },
      onUsage: (u) => {
        if (opts.emit && opts.nodeId) void opts.emit({ type: 'node:tokens', id: opts.nodeId, ...u });
      },
    });
  } catch (err) {
    console.error(
      `[fixPlanner/fix] batch fix call FAILED for cluster=${opts.cluster.id}: ${err instanceof Error ? err.message : String(err)}`,
    );
    throw err;
  }

  // Per-file gates: drop any accepted file whose content is unchanged, fails the
  // hallucinated-API lint, trips the shrink-guard, or introduces an unresolvable
  // project-internal import. Keep request order.
  const editAttempts: EditAttempt[] = [];
  const changedFiles: ChangedFile[] = [];
  // Track which spec files the fix batch edited (assertions changed) for reflection.
  const editedSpecs: EditedSpec[] = [];
  let acceptedCount = 0;

  // ── SURGICAL — search/replace blocks via SearchReplaceStrategy (the ONLY
  // apply path; there is no whole-file fallback). Parse + reconcile against
  // the candidate set in REQUEST order (only candidate files are accepted;
  // apply order is request-ordered = deterministic). ──
  const parsed = parseBatchEditOutput(raw);
  const rec = reconcileBatchEdit(candidatePaths, parsed);
  acceptedCount = rec.accepted.length;

  if (rec.extra.length > 0) {
    console.warn(
      `[fixPlanner/fix] cluster=${opts.cluster.id}: dropped ${rec.extra.length} non-candidate file(s) the model returned: ` +
        rec.extra.map((f) => f.path).join(', '),
    );
    // Structured feedback (not just a console.warn) so the NEXT iteration's
    // fixAttemptHistory shows exactly why a write did nothing — otherwise a
    // model that keeps writing the same non-candidate path has no way to
    // learn it's not editable for this cluster.
    for (const f of rec.extra) {
      editAttempts.push({
        strategy: 'search-replace',
        outcome: 'parse-failed',
        detail: `wrote non-candidate file ${f.path} — only [${candidatePaths.join(', ')}] are editable for this cluster`,
      });
    }
  }

  // Import-resolution gate operates on the batch AS A WHOLE: a project-internal
  // import an accepted file introduces must resolve against the WORKSPACE or
  // another file WRITTEN IN THIS SAME BATCH (e.g. the model both rewrites an
  // import and creates the module it now points to, in one response).
  const batchPaths = new Set(rec.accepted.map((f) => stripSlash(f.path)));
  const batchKnownPaths = new Set<string>([...fileMap.keys(), ...batchPaths]);

  for (const f of rec.accepted as BatchEditFile[]) {
    const path = stripSlash(f.path);
    if (f.blocks.length === 0) {
      // Deliberate no-op ("no change to this file") — skip silently.
      continue;
    }
    const original = fileMap.get(path); // undefined ⇒ creatable candidate (doesn't exist yet)

    let newCode: string;
    const emptySearchBlock = f.blocks.find((b) => b.search === '');
    if (emptySearchBlock) {
      // "search":"" ⇒ CREATE this file — the ONLY whole-content case. Only
      // legitimate for a candidate that genuinely doesn't exist yet (the
      // TS2307 create path, ported surgically). An EXISTING file must always
      // be edited with a real SEARCH snippet — never rewritten wholesale.
      const isCreatable = creatableCandidates.has(path) || original === undefined;
      if (!isCreatable) {
        editAttempts.push({
          strategy: 'search-replace',
          outcome: 'no-match',
          detail: `empty SEARCH is only valid to CREATE a new file; ${path} already exists — use a real SEARCH snippet copied from its current contents to edit it`,
        });
        continue;
      }
      newCode = emptySearchBlock.replace;
    } else {
      const strat = new SearchReplaceStrategy();
      const r = strat.apply(JSON.stringify({ blocks: f.blocks }), original ?? '', path);
      if (!r.ok) {
        console.warn(`[fixPlanner/fix] ${path}: surgical edit NOT applied for cluster=${opts.cluster.id} — ${r.reason}: ${r.detail}`);
        editAttempts.push({ strategy: 'search-replace', outcome: r.reason, detail: r.detail });
        continue;
      }
      newCode = r.newCode;
    }

    if (newCode === (original ?? '')) {
      // No-op result — skip silently (not a fix, not a failure).
      continue;
    }

    const lint = lintHallucinatedAPI(newCode);
    if (!lint.ok) {
      console.error(
        `[fixPlanner/fix] rejecting ${path} for cluster=${opts.cluster.id} — hallucinated API: ` +
          `this.${lint.hit}(...) at line ${lint.line}`,
      );
      editAttempts.push({ strategy: 'search-replace', outcome: 'parse-failed', detail: `hallucinated API: ${lint.hit} at line ${lint.line}` });
      continue;
    }
    // The whole-file SHRINK guard is N/A here — a block only ever touches
    // the text it quoted, so an "unseen tail" data-loss event (the guard's
    // real target) can't happen. Log-only heads-up for a large net deletion
    // in a single block (never a hard reject — see design doc).
    warnOnLargeNetDeletion(opts.cluster.id, path, original ?? '', newCode);
    const unresolvable = firstUnresolvableImport(path, newCode, batchKnownPaths);
    if (unresolvable) {
      const detail = `unresolvable import '${unresolvable}' in ${path} — no such module in workspace or batch; use the manifest or create the module in the same batch`;
      console.error(`[fixPlanner/fix] rejecting ${path} for cluster=${opts.cluster.id} — ${detail}`);
      editAttempts.push({ strategy: 'search-replace', outcome: 'parse-failed', detail });
      continue;
    }
    editAttempts.push({ strategy: 'search-replace', outcome: 'success' });
    changedFiles.push({ file: path, newCode, notes: f.notes.length > 0 ? f.notes : ['fix: surgical search/replace edit'] });
    if (isSpecPath(path)) {
      const before = extractAssertions(original ?? '');
      const after = extractAssertions(newCode);
      if (before.lines.join('\n') !== after.lines.join('\n')) {
        editedSpecs.push({ path, before: original ?? '', after: newCode });
      }
    }
  }

  console.log(
    `[fixPlanner/fix] END   cluster=${opts.cluster.id} accepted=${changedFiles.length}/${acceptedCount} ` +
      `editedSpecs=${editedSpecs.length} elapsedMs=${Date.now() - fixStart} strategy=search-replace`,
  );

  if (changedFiles.length === 0) {
    activity('No usable file change (all candidates were no-op or rejected). Will retry next iteration with a different angle.');
    return { changedFiles: [], raw, editAttempts };
  }

  appendFixTrace({
    ts: new Date().toISOString(),
    clusterId: opts.cluster.id,
    rootHypothesis: opts.cluster.rootHypothesis,
    wouldFixAll: opts.cluster.wouldFixAll,
    relevantFiles: opts.cluster.relevantFiles,
    changed: changedFiles.map((c) => c.file),
    editedSpecs: editedSpecs.map((s) => s.path),
    failuresCount: opts.failures.length,
    firstFailure: opts.failures[0]
      ? {
          file: opts.failures[0].file,
          suite: opts.failures[0].suite,
          test: opts.failures[0].testName,
          error: opts.failures[0].error.slice(0, 600),
        }
      : null,
    changedExcerpts: changedFiles.map((c) => ({ file: c.file, newCodeExcerpt: excerpt(c.newCode, 60) })),
  });

  // ── REFLECTION (anti-cheat) ─────────────────────────────────────────────
  // When the fix batch edited a spec's ASSERTIONS, audit before→after: if it
  // weakened/loosened an assertion to force a pass, rewrite the test correctly
  // AND fix the code. Reflection edits WIN on a path conflict (the audited file
  // supersedes the cheating one). The current view passed to reflection is the
  // post-batch state (apply changedFiles over fileMap), so the auditor sees the
  // edited test + the code as the batch left it.
  if (editedSpecs.length > 0) {
    activity(`A test's assertions were edited — auditing for weakened assertions (anti-cheat) before committing.`);
    const postBatch = new Map(relevantFiles);
    for (const c of changedFiles) postBatch.set(stripSlash(c.file), c.newCode);
    let reflectionEdits: ChangedFile[] = [];
    try {
      reflectionEdits = await reflectOnTestEdits({
        editedSpecs,
        relevantFiles: postBatch,
        designDocs: opts.designDocs ?? [],
        // Same deterministic signal diagnose gets: when history shows a
        // fix-A-breaks-B oscillation, a setup-repairing spec edit is the fix,
        // not a cheat — without this the auditor reverts the repair and
        // re-injects the code edit that caused the rolled-back regressions.
        contradictions: detectTestContradictions(opts.fixAttemptHistory ?? []),
        llm: editLlm,
        llmCall: opts.llmCall,
        signal: opts.signal,
      });
    } catch (err) {
      console.warn(
        `[fixPlanner/fix] reflection threw (keeping the fix batch): ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    if (reflectionEdits.length > 0) {
      console.log(
        `[fixPlanner/fix] cluster=${opts.cluster.id}: assertion-reflection rewrote ${reflectionEdits.length} file(s) ` +
          `(weakened assertion repaired): ${reflectionEdits.map((c) => c.file).join(', ')}`,
      );
      mergeReflectionEdits(changedFiles, reflectionEdits, fileMap);
    }
  }

  // Runbook authoring (flag-gated): key off the first changed file as a
  // non-load-bearing default (the runbook path is off by default).
  let runbook: RunbookCandidate | undefined;
  if (opts.enableRunbooks && changedFiles.length > 0) {
    const primary = changedFiles[0];
    const authored = await requestRunbookCandidate({
      hypothesis: opts.cluster.rootHypothesis,
      failures: opts.failures.map((f) => ({ error: f.error })),
      oldCode: fileMap.get(stripSlash(primary.file)) ?? '',
      newCode: primary.newCode,
      llmCall: opts.llmCall ?? callLLMStream,
      llm: opts.llm,
      signal: opts.signal,
    });
    runbook = authored ?? undefined;
  }

  return { changedFiles, raw: '', editAttempts, runbook };
}

/** Resolve the injected llm-call or the default `callLLMStream`. */
function callOrInjected(injected?: StreamLlmCall): StreamLlmCall {
  return injected ?? callLLMStream;
}

/**
 * Merge reflection edits into `changedFiles`, reflection winning on a path
 * conflict (it replaces the cheating file's entry in place, preserving order),
 * and any NEW file (e.g. the code the auditor edited to satisfy a corrected
 * assertion) appended in a deterministic (sorted) order.
 */
function mergeReflectionEdits(
  changedFiles: ChangedFile[],
  reflectionEdits: ChangedFile[],
  fileMap: Map<string, string>,
): void {
  const byPath = new Map(changedFiles.map((c) => [stripSlash(c.file), c]));
  const appended: ChangedFile[] = [];
  for (const r of reflectionEdits) {
    const k = stripSlash(r.file);
    const original = fileMap.get(k) ?? '';
    if (r.newCode === original) continue; // reflection produced a no-op for this file
    const existing = byPath.get(k);
    if (existing) {
      existing.newCode = r.newCode;
      existing.notes = r.notes;
    } else {
      appended.push({ file: k, newCode: r.newCode, notes: r.notes });
    }
  }
  appended.sort((a, b) => (a.file < b.file ? -1 : a.file > b.file ? 1 : 0));
  changedFiles.push(...appended);
}
